import React from "react";

import { faEdit } from "@fortawesome/pro-regular-svg-icons";
import { Controller, useFormContext } from "react-hook-form";

import { FormItem } from "src/base-components/FormItem";
import { Input } from "src/base-components/Input";
import { Textarea } from "src/base-components/Textarea";
import { ErrorHint } from "src/design-system/ErrorHint";
import { IconButton } from "src/design-system/Icon";
import { Tooltip } from "src/design-system/Tooltip";
import type {
  AICredentialFormInputs,
  CredentialEnvName,
} from "src/settings/credentials/model";

type Props = {
  env: CredentialEnvName;
  isEditing: boolean;
  secretEditable: boolean;
  onEnableSecret: () => void;
};

export const VertexFields: React.FC<Props> = ({
  env,
  isEditing,
  secretEditable,
  onEnableSecret,
}) => {
  const {
    register,
    control,
    formState: { errors },
  } = useFormContext<AICredentialFormInputs>();

  const jsonField = `envConfigs.${env}.serviceAccountJson` as const;
  const projectField = `envConfigs.${env}.projectId` as const;
  const locationField = `envConfigs.${env}.location` as const;

  const jsonError = errors.envConfigs?.[env]?.serviceAccountJson;

  const showMasked = isEditing && !secretEditable;

  return (
    <>
      <FormItem gap="sm" label="Service Account JSON" isRequired>
        <div className="flex w-full items-center gap-x-2">
          <div className="flex-1">
            {showMasked ? (
              <Input
                placeholder="••••••••"
                type="password"
                disabled
                fullWidth
              />
            ) : (
              <Controller
                control={control}
                name={jsonField}
                rules={{
                  required: secretEditable
                    ? "Service account JSON is required"
                    : false,
                  validate: {
                    json: (v) => {
                      if (!v) return true;
                      try {
                        JSON.parse(v);
                        return true;
                      } catch {
                        return "Invalid JSON format";
                      }
                    },
                    fields: (v) => {
                      if (!v) return true;
                      try {
                        const data = JSON.parse(v);
                        const required = [
                          "type",
                          "project_id",
                          "private_key",
                          "client_email",
                        ];
                        const missing = required.filter((f) => !(f in data));
                        if (missing.length > 0) {
                          return `Missing required fields: ${missing.join(", ")}`;
                        }
                        return true;
                      } catch {
                        return true; // JSON parse error handled by json validator
                      }
                    },
                    type: (v) => {
                      if (!v) return true;
                      try {
                        const data = JSON.parse(v);
                        if (
                          data.type !== undefined &&
                          data.type !== "service_account"
                        ) {
                          return "Type must be 'service_account'";
                        }
                        return true;
                      } catch {
                        return true;
                      }
                    },
                  },
                }}
                render={({ field }) => (
                  <Textarea
                    placeholder='{"type": "service_account", ...}'
                    rows={6}
                    value={field.value ?? ""}
                    fullWidth
                    onChange={(e) => field.onChange(e.target.value)}
                  />
                )}
              />
            )}
          </div>
          {showMasked && (
            <Tooltip
              placement="right"
              title="Edit service account JSON"
              asChild
            >
              <IconButton icon={faEdit} size="xs" onClick={onEnableSecret} />
            </Tooltip>
          )}
        </div>
        {jsonError?.message && <ErrorHint>{jsonError.message}</ErrorHint>}
      </FormItem>
      <FormItem gap="sm" label="Project ID">
        <Input
          placeholder="my-gcp-project"
          fullWidth
          {...register(projectField)}
        />
      </FormItem>
      <FormItem gap="sm" label="Location">
        <Input
          placeholder="us-central1"
          fullWidth
          {...register(locationField)}
        />
      </FormItem>
    </>
  );
};
